import { useMemo, useState } from 'react'
import { ESTADOS_PUBLICACION, PILARES, TIPOS_CONTENIDO, type Rec } from '../../shared/schema'
import { createRecord, quickUpdate, useRecords } from '../lib/api'
import { useEditor } from '../lib/useOpen'
import { Link } from '../lib/router'
import { fmtDay, MESES, today, ymd } from '../lib/format'
import { addDays, CalNav, DaysColumns, Kanban, monthLabel, MonthGrid, startOfWeek, useCursor } from '../components/Boards'
import { RecordForm } from '../components/RecordForm'
import { Avatar, Badge, Button, copyText, Empty, ErrorNote, Icon, PageHeader, Segmented, Skeleton, Star, toneOf, useToast } from '../components/ui'

export const PILLAR_COLORS: Record<string, string> = {
  'Build in Public': '#5A6B5C',
  'Aprendemos Juntas': '#B38B4D',
  'Hay más de lo que ves': '#C46D51',
  'La casa de tus sueños': '#8A5A4E',
  'Invertir desde afuera': '#46606E',
  'Leti & Yesi': '#A8707F',
}
export const pillarColor = (p?: string) => PILLAR_COLORS[p ?? ''] ?? '#B9A88C'
const PILLAR_DESC: Record<string, string> = {
  'Build in Public': 'Cómo estamos construyendo REVELA',
  'Aprendemos Juntas': 'Aprendizajes del mundo inmobiliario',
  'Hay más de lo que ves': 'Nuestra filosofía y mirada',
  'La casa de tus sueños': 'Hogares, diseño y estilo de vida',
  'Invertir desde afuera': 'Invertir en Uruguay desde otro país',
  'Leti & Yesi': 'Nuestra historia como socias',
}

type View = 'mes' | 'semana' | 'tablero' | 'banco' | 'plan'

export function Marketing() {
  const { data, loading, error, refresh } = useRecords('marketing')
  const ed = useEditor(data)
  const toast = useToast()
  const [view, setView] = useState<View>('mes')
  const [pillars, setPillars] = useState<string[]>([])
  const { cursor, setCursor } = useCursor()

  const posts = data.filter(m => (m.tipo || 'Publicación') === 'Publicación')
  const visible = posts.filter(m => !pillars.length || pillars.includes(m.pilar))
  const monthKey = ymd(cursor).slice(0, 7)
  const inMonth = posts.filter(p => p.fecha?.startsWith(monthKey))
  const count = (e: string) => inMonth.filter(p => p.estado === e).length

  const move = async (r: Rec, patch: Record<string, any>, msg: string) => {
    try { await quickUpdate('marketing', r, patch); toast(msg) } catch (e: any) { toast(e.message, 'error') }
  }
  const newPost = (d: Record<string, any> = {}) => ed.create({ tipo: 'Publicación', estado: 'Idea', formato: 'Reel', ...d })

  return (
    <div className="page marketing">
      <PageHeader eyebrow="Módulo 5" title="Marketing y feed" subtitle="Calendario editorial de @revela.real.estate. Se planifica acá; no se publica automáticamente."
        actions={<div className="row gap-s wrap"><Link to="/ideas" className="btn btn-ghost btn-md"><Icon name="sparkle" size={17} /><span>Banco de ideas</span></Link><Button variant="accent" icon="plus" onClick={() => newPost({ fecha: today() })}>Nueva publicación</Button></div>} />

      <section className="mk-indicators">
        <div className="mk-month">{MESES[cursor.getMonth()]}</div>
        <Indicator label="Ideas pendientes" value={count('Idea')} tone="sand" />
        <Indicator label="Grabadas" value={count('Grabado')} tone="terra" />
        <Indicator label="Publicadas" value={count('Publicado')} tone="olive" />
        <div className="mk-progress" aria-label="Avance del mes">
          {ESTADOS_PUBLICACION.map(e => <span key={e} className={`tone-${toneOf(e)}`} style={{ flex: count(e) || 0.0001 }} />)}
        </div>
      </section>

      <div className="toolbar">
        <Segmented value={view} onChange={setView} options={[
          { value: 'mes', label: 'Mes', icon: 'calendar' },
          { value: 'semana', label: 'Semana', icon: 'list' },
          { value: 'tablero', label: 'Tablero', icon: 'kanban' },
          { value: 'banco', label: 'Banco de contenido', icon: 'grid' },
          { value: 'plan', label: 'Plan 30 días', icon: 'layers' },
        ]} />
      </div>
      {view !== 'banco' && (
        <div className="pillar-filter" role="group" aria-label="Filtrar por pilar">
          {PILARES.map(p => {
            const on = pillars.includes(p)
            return (
              <button key={p} type="button" className={`pillar-chip ${on ? 'on' : ''} ${pillars.length && !on ? 'dim' : ''}`} style={{ ['--pc' as any]: pillarColor(p) }}
                onClick={() => setPillars(on ? pillars.filter(x => x !== p) : [...pillars, p])} aria-pressed={on}>
                <span className="pc-dot" />{p}
              </button>
            )
          })}
          {pillars.length > 0 && <button type="button" className="link-btn" onClick={() => setPillars([])}>Ver todos</button>}
        </div>
      )}

      {error && <ErrorNote error={error} onRetry={refresh} />}
      {loading ? <Skeleton rows={4} /> : (
        <>
          {view === 'mes' && (
            <>
              <CalNav label={monthLabel(cursor)} onPrev={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} onNext={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} onToday={() => setCursor(new Date())} />
              <MonthGrid month={cursor} items={visible} dateOf={r => r.fecha} onItemClick={ed.open}
                onDayClick={d => newPost({ fecha: d })}
                onMove={(r, d) => move(r, { fecha: d }, `Movida al ${fmtDay(d)}`)}
                render={r => <PostChip r={r} />} />
              <p className="hint">Arrastrá una publicación a otro día para reprogramarla. Tocá un día vacío para crear una.</p>
            </>
          )}
          {view === 'semana' && (() => {
            const start = startOfWeek(cursor)
            return (
              <>
                <CalNav label={`Semana del ${start.getDate()} de ${MESES[start.getMonth()]}`} onPrev={() => setCursor(addDays(cursor, -7))} onNext={() => setCursor(addDays(cursor, 7))} onToday={() => setCursor(new Date())} />
                <DaysColumns start={start} days={7} items={visible} dateOf={r => r.fecha} onItemClick={ed.open} onDayClick={d => newPost({ fecha: d })}
                  onMove={(r, d) => move(r, { fecha: d }, `Movida al ${fmtDay(d)}`)} render={r => <PostCard r={r} compact />} />
              </>
            )
          })()}
          {view === 'tablero' && (
            <Kanban columns={ESTADOS_PUBLICACION.map(e => ({ key: e, tone: toneOf(e) }))} items={visible} groupBy={r => r.estado} onCardClick={ed.open}
              onMove={(r, e) => move(r, { estado: e }, `“${r.titulo}” → ${e}`)} render={r => <PostCard r={r} />} />
          )}
          {view === 'banco' && <Banco data={data} onOpen={ed.open} onNew={t => ed.create({ tipo: t, estado: 'Idea' })} />}
          {view === 'plan' && <Plan30 posts={posts} onOpen={ed.open} onNew={newPost} />}
        </>
      )}

      <RecordForm col="marketing" open={ed.isOpen} record={ed.editing} defaults={ed.creating ?? undefined} onClose={ed.close}
        top={d => <CopyBar d={d} />}
        actions={rec => rec && (
          <>
            <Button size="sm" variant="ghost" icon="copy" onClick={async () => {
              const { id, ref, createdAt, updatedAt, ...rest } = rec
              try { const c = await createRecord('marketing', { ...rest, titulo: `${rec.titulo} (copia)`, estado: 'Idea' }); toast('Publicación duplicada'); ed.open(c) } catch (e: any) { toast(e.message, 'error') }
            }}>Duplicar</Button>
            {rec.estado !== 'Publicado' && <Button size="sm" variant="ghost" icon="check" onClick={async () => {
              const next = rec.estado === 'Idea' ? 'Grabado' : 'Publicado'
              try { const s = await quickUpdate('marketing', rec, { estado: next }); toast(`Marcada como ${next}`); ed.open(s) } catch (e: any) { toast(e.message, 'error') }
            }}>Marcar {rec.estado === 'Idea' ? 'grabada' : 'publicada'}</Button>}
          </>
        )} />
    </div>
  )
}

const Indicator = ({ label, value, tone }: { label: string; value: number; tone: string }) => (
  <div className="mk-ind"><span className={`dot tone-${tone}`} /><strong>{value}</strong><span>{label}</span></div>
)

function PostChip({ r }: { r: Rec }) {
  return (
    <span className="post-chip" style={{ ['--pc' as any]: pillarColor(r.pilar) }}>
      {r.portada ? <img src={r.portada} alt="" /> : <span className="pc-bar" />}
      <span className="pc-title">{r.titulo}</span>
      <span className={`state-dot tone-${toneOf(r.estado)}`} title={r.estado} />
    </span>
  )
}

function PostCard({ r, compact }: { r: Rec; compact?: boolean }) {
  return (
    <div className={`post-card ${compact ? 'compact' : ''}`} style={{ ['--pc' as any]: pillarColor(r.pilar) }}>
      <div className="post-cover">
        {r.portada ? <img src={r.portada} alt="" /> : <div className="post-cover-ph"><Star size={16} color="rgba(255,255,255,.85)" /><span>{r.formato || 'Post'}</span></div>}
      </div>
      <div className="post-meta">
        <span className="post-pillar">{r.pilar || 'Sin pilar'}</span>
        <strong>{r.titulo}</strong>
        <div className="post-foot"><span>{r.fecha ? fmtDay(r.fecha) : 'Sin fecha'} · {r.formato}</span>{!compact && <Badge>{r.estado}</Badge>}<Avatar name={r.socia} /></div>
      </div>
    </div>
  )
}

function CopyBar({ d }: { d: Record<string, any> }) {
  const toast = useToast()
  const items = [['Guion', d.guion], ['Copy', d.copy], ['Hashtags', d.hashtags], ['Copy + hashtags', [d.copy, d.hashtags].filter(Boolean).join('\n\n')]].filter(([, v]) => v)
  if (!items.length) return null
  return (
    <div className="copy-bar">
      <span className="ctx-label">Copiar rápido</span>
      {items.map(([k, v]) => <Button key={k} size="sm" variant="quiet" icon="copy" onClick={async () => { const ok = await copyText(v); toast(ok ? `${k} copiado` : 'No se pudo copiar; seleccioná el texto a mano', ok ? 'ok' : 'error') }}>{k}</Button>)}
    </div>
  )
}

/* ---------------------------- Banco de contenido ---------------------------- */

function Banco({ data, onOpen, onNew }: { data: Rec[]; onOpen: (r: Rec) => void; onNew: (tipo: string) => void }) {
  const toast = useToast()
  const [tipo, setTipo] = useState('')
  const [q, setQ] = useState('')
  const [tag, setTag] = useState('')
  const [estado, setEstado] = useState('')
  const tags = useMemo(() => [...new Set(data.flatMap(d => d.etiquetas ?? []))], [data])
  const list = data.filter(d =>
    (!tipo || (d.tipo || 'Publicación') === tipo) && (!tag || (d.etiquetas ?? []).includes(tag)) && (!estado || d.estado === estado) &&
    (!q || [d.titulo, d.copy, d.guion, d.idea, d.hashtags].join(' ').toLowerCase().includes(q.toLowerCase())))
  const groups = [
    { label: 'Ideas pendientes', f: (d: Rec) => d.estado === 'Idea' && (d.tipo || 'Publicación') === 'Publicación' },
    { label: 'Guiones de reels', f: (d: Rec) => d.tipo === 'Guion' || (d.formato === 'Reel' && d.guion) },
    { label: 'Textos listos', f: (d: Rec) => d.tipo === 'Texto listo' || (!!d.copy && d.estado !== 'Publicado' && d.tipo === 'Publicación') },
    { label: 'Frases de marca', f: (d: Rec) => d.tipo === 'Frase de marca' },
    { label: 'Fotos y referencias', f: (d: Rec) => d.tipo === 'Foto' || d.tipo === 'Referencia visual' },
    { label: 'Carruseles en preparación', f: (d: Rec) => d.formato === 'Carrusel' && d.estado !== 'Publicado' },
    { label: 'Publicado', f: (d: Rec) => d.estado === 'Publicado' },
  ]
  return (
    <div className="banco">
      <div className="filters">
        <div className="search-input"><Icon name="search" size={16} /><input id="banco-q" type="search" placeholder="Buscar en guiones, copys e ideas" value={q} onChange={e => setQ(e.target.value)} aria-label="Buscar en el banco" /></div>
        <div className="filter-row">
          <select id="banco-tipo" value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo"><option value="">Todos los tipos</option>{TIPOS_CONTENIDO.map(t => <option key={t}>{t}</option>)}</select>
          <select id="banco-estado" value={estado} onChange={e => setEstado(e.target.value)} aria-label="Estado"><option value="">Todos los estados</option>{ESTADOS_PUBLICACION.map(t => <option key={t}>{t}</option>)}</select>
          <select id="banco-tag" value={tag} onChange={e => setTag(e.target.value)} aria-label="Etiqueta"><option value="">Todas las etiquetas</option>{tags.map(t => <option key={t}>{t}</option>)}</select>
          <select id="banco-new" value="" onChange={e => e.target.value && onNew(e.target.value)} aria-label="Agregar al banco"><option value="">+ Agregar al banco…</option>{TIPOS_CONTENIDO.map(t => <option key={t}>{t}</option>)}</select>
        </div>
      </div>
      {(q || tipo || tag || estado) ? (
        <BancoGrid list={list} onOpen={onOpen} toast={toast} />
      ) : groups.map(g => {
        const l = data.filter(g.f)
        return l.length ? <section key={g.label} className="banco-group"><h3>{g.label} <span className="count">{l.length}</span></h3><BancoGrid list={l} onOpen={onOpen} toast={toast} /></section> : null
      })}
      {!data.length && <Empty title="El banco está vacío" text="Guardá acá ideas, guiones, frases y referencias para reutilizarlas." />}
    </div>
  )
}

function BancoGrid({ list, onOpen, toast }: { list: Rec[]; onOpen: (r: Rec) => void; toast: ReturnType<typeof useToast> }) {
  return (
    <div className="banco-grid">
      {list.map(d => (
        <article key={d.id} className="banco-card" style={{ ['--pc' as any]: pillarColor(d.pilar) }} onClick={() => onOpen(d)} tabIndex={0}>
          {d.portada || d.imagenes?.[0] ? <img src={d.portada || d.imagenes[0]} alt="" /> : null}
          <div className="banco-type">{d.tipo || 'Publicación'}{d.pilar && <span> · {d.pilar}</span>}</div>
          <strong className={d.tipo === 'Frase de marca' ? 'quote' : ''}>{d.tipo === 'Frase de marca' ? d.copy || d.titulo : d.titulo}</strong>
          {d.tipo !== 'Frase de marca' && (d.guion || d.copy || d.idea) && <p>{String(d.guion || d.copy || d.idea).slice(0, 110)}</p>}
          <div className="banco-foot">
            <div className="chips">{(d.etiquetas ?? []).map((t: string) => <span key={t} className="chip xs">{t}</span>)}</div>
            <div className="row gap-xs" onClick={e => e.stopPropagation()}>
              {(d.copy || d.guion) && <button type="button" className="icon-btn sm" aria-label="Copiar texto" title="Copiar texto" onClick={async () => toast((await copyText(d.copy || d.guion)) ? 'Texto copiado' : 'No se pudo copiar')}><Icon name="copy" size={14} /></button>}
              <button type="button" className="icon-btn sm" aria-label="Reutilizar como publicación" title="Reutilizar como publicación" onClick={async () => {
                try {
                  await createRecord('marketing', { tipo: 'Publicación', titulo: d.titulo, pilar: d.pilar, formato: d.formato || 'Reel', guion: d.guion, copy: d.copy, hashtags: d.hashtags, idea: d.idea, estado: 'Idea', etiquetas: d.etiquetas, fecha: today() })
                  toast('Creada una publicación nueva para hoy en el calendario')
                } catch (e: any) { toast(e.message, 'error') }
              }}><Icon name="refresh" size={14} /></button>
            </div>
          </div>
        </article>
      ))}
    </div>
  )
}

/* ------------------------------ Plan 30 días ------------------------------ */

function Plan30({ posts, onOpen, onNew }: { posts: Rec[]; onOpen: (r: Rec) => void; onNew: (d: Record<string, any>) => void }) {
  const start = new Date(); start.setHours(0, 0, 0, 0)
  const days = Array.from({ length: 30 }, (_, i) => ymd(addDays(start, i)))
  const inRange = posts.filter(p => p.fecha && p.fecha >= days[0] && p.fecha <= days[29])
  const total = inRange.length
  const ideal = total / PILARES.length
  const stats = PILARES.map(p => ({ p, n: inRange.filter(x => x.pilar === p).length }))
  const max = Math.max(1, ...stats.map(s => s.n))
  const faltan = stats.filter(s => s.n < Math.max(1, Math.floor(ideal)))
  const balance = total ? 1 - stats.reduce((s, x) => s + Math.abs(x.n - ideal), 0) / (2 * total) : 0
  const libres = days.filter(d => !inRange.some(p => p.fecha === d))
  return (
    <div className="plan30">
      <section className="panel pad">
        <div className="plan-head">
          <div><h3>Equilibrio entre pilares</h3><p className="muted">{total} publicaciones en los próximos 30 días · ideal {ideal.toFixed(1)} por pilar</p></div>
          <div className="balance"><strong>{Math.round(balance * 100)} %</strong><span>{balance >= 0.8 ? 'Bien repartido' : balance >= 0.6 ? 'Casi equilibrado' : 'Desbalanceado'}</span></div>
        </div>
        <div className="pillar-bars">
          {stats.map(s => (
            <div key={s.p} className="pbar">
              <div className="pbar-label"><span className="pc-dot" style={{ background: pillarColor(s.p) }} /><strong>{s.p}</strong><small>{PILLAR_DESC[s.p]}</small></div>
              <div className="pbar-track">
                <span className="pbar-fill" style={{ width: `${(s.n / max) * 100}%`, background: pillarColor(s.p) }} />
                {total > 0 && <span className="pbar-ideal" style={{ left: `${Math.min(100, (ideal / max) * 100)}%` }} title="Ideal" />}
              </div>
              <span className="pbar-n">{s.n}</span>
              <button type="button" className="icon-btn sm" aria-label={`Agregar idea de ${s.p}`} title="Agregar idea" onClick={() => onNew({ pilar: s.p, fecha: libres[0] ?? days[0] })}><Icon name="plus" size={14} /></button>
            </div>
          ))}
        </div>
        {faltan.length > 0 && total > 0 && <p className="hint"><Star size={11} /> Faltan publicaciones de {faltan.map(f => f.p).join(', ')}. El botón + crea una idea en el próximo día libre.</p>}
      </section>

      <section className="strip30">
        {days.map(d => {
          const ps = inRange.filter(p => p.fecha === d)
          const date = new Date(d + 'T12:00:00')
          return (
            <div key={d} className={`s30 ${d === today() ? 'is-today' : ''} ${ps.length ? '' : 'free'}`}>
              <button type="button" className="s30-head" onClick={() => onNew({ fecha: d })} aria-label={`Agregar el ${fmtDay(d)}`}>
                <span>{['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][date.getDay()]}</span><strong>{date.getDate()}</strong>
              </button>
              {ps.map(p => <button type="button" key={p.id} className="s30-post" style={{ ['--pc' as any]: pillarColor(p.pilar) }} onClick={() => onOpen(p)} title={`${p.titulo} · ${p.pilar}`}><span>{p.titulo}</span></button>)}
            </div>
          )
        })}
      </section>
    </div>
  )
}
